import type { JSX } from "react";
import { cn } from "./cn";
import { Icon } from "./Icon";
import type { IconName } from "./Icon";

export type MeetingCardDateColor = "accent" | "blue" | "green";

export interface MeetingCardProps {
  /** Day number (e.g. "14") */
  readonly day: string;
  /** Month abbreviation (e.g. "FEB") */
  readonly month: string;
  /** Date block background color */
  readonly dateColor?: MeetingCardDateColor;
  /** Event title */
  readonly title: string;
  /** Time/date description */
  readonly time: string;
  /** Platform icon name */
  readonly platformIcon?: IconName;
  /** Platform label (e.g. "Zoom", "Telegram") */
  readonly platformLabel?: string;
  /** Platform text color */
  readonly platformColor?: "blue" | "purple" | "green";
  /** Click handler */
  readonly onClick?: () => void;
  /** Extra className */
  readonly className?: string;
}

const dateColorMap: Readonly<Record<MeetingCardDateColor, string>> = {
  accent: "bg-accent",
  blue: "bg-blue",
  green: "bg-green",
};

const platformColorMap: Readonly<Record<NonNullable<MeetingCardProps["platformColor"]>, string>> = {
  blue: "text-blue",
  purple: "text-purple",
  green: "text-green",
};

/**
 * Meeting/event card with date block, title, time, and platform.
 * Matches B1 design spec — reusable for calendar events, scheduled meetings.
 */
export function MeetingCard({
  day,
  month,
  dateColor = "accent",
  title,
  time,
  platformIcon,
  platformLabel,
  platformColor = "blue",
  onClick,
  className,
}: MeetingCardProps): JSX.Element {
  const Component = onClick ? "button" : "div";
  return (
    <Component
      type={onClick ? "button" : undefined}
      className={cn(
        "flex items-center gap-4 bg-surface-secondary rounded-xl px-4 py-3.5",
        "border border-edge",
        onClick && "cursor-pointer hover:bg-surface-hover transition-colors text-left w-full",
        className,
      )}
      onClick={onClick}
    >
      <div
        className={cn(
          "w-[52px] min-w-[52px] h-[52px] rounded-xl flex flex-col items-center justify-center",
          "text-white font-bold",
          dateColorMap[dateColor],
        )}
      >
        <span className="text-[22px] leading-none">{day}</span>
        <span className="text-[10px] font-semibold opacity-80 uppercase">{month}</span>
      </div>
      <div className="flex-1 min-w-0 flex flex-col gap-1">
        <span className="text-[14px] font-semibold text-[#E8EDEF] truncate">{title}</span>
        <span className="text-xs text-content-muted">{time}</span>
        {platformIcon && platformLabel ? (
          <div className={cn("flex items-center gap-1.5 text-xs font-medium", platformColorMap[platformColor])}>
            <Icon name={platformIcon} size={13} />
            {platformLabel}
          </div>
        ) : null}
      </div>
    </Component>
  );
}
